import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { apiClient } from '../../services/apiClient';
import LoadingSpinner from '../common/LoadingSpinner';

const CompanyData = () => {
  const [pastPerformanceDocs, setPastPerformanceDocs] = useState([]);
  const [resumeDocs, setResumeDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadingPP, setUploadingPP] = useState(false);
  const [uploadingResume, setUploadingResume] = useState(false);
  const [searchPP, setSearchPP] = useState('');
  const [searchResume, setSearchResume] = useState('');
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [error, setError] = useState('');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    loadDocuments();
  }, []);

  const loadDocuments = async () => {
    try {
      setLoading(true);
      setError('');
      const [ppResponse, resumeResponse] = await Promise.all([
        apiClient.getCompanyDocuments('past_performance'),
        apiClient.getCompanyDocuments('resume')
      ]);

      if (ppResponse.success) {
        setPastPerformanceDocs(ppResponse.data.documents || []);
      }
      if (resumeResponse.success) {
        setResumeDocs(resumeResponse.data.documents || []);
      }
    } catch (err) {
      setError(err.message || 'Failed to load company documents');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (file, documentType) => {
    const setUploading = documentType === 'past_performance' ? setUploadingPP : setUploadingResume;

    try {
      setUploading(true);
      setError('');

      const response = await apiClient.uploadCompanyDocument(file, documentType);

      if (response.success) {
        // Reload documents immediately
        await loadDocuments();

        // Show success message
        setSuccessMessage(`${file.name} uploaded successfully! Processing text extraction in background.`);
        setTimeout(() => setSuccessMessage(''), 5000);

        // Start polling to update status
        startPollingForProcessing(response.data.documentId, documentType);
      } else {
        throw new Error(response.message || 'Upload failed');
      }
    } catch (err) {
      setErrorMessage(`Upload failed: ${err.message}`);
      setTimeout(() => setErrorMessage(''), 5000);
    } finally {
      setUploading(false);
    }
  };

  const startPollingForProcessing = (documentId, documentType) => {
    const pollInterval = setInterval(async () => {
      try {
        // Reload documents to get updated status
        const response = documentType === 'past_performance'
          ? await apiClient.getCompanyDocuments('past_performance')
          : await apiClient.getCompanyDocuments('resume');

        if (response.success) {
          const docs = response.data.documents || [];
          const doc = docs.find(d => d.document_id === documentId);

          // Update the appropriate state
          if (documentType === 'past_performance') {
            setPastPerformanceDocs(docs);
          } else {
            setResumeDocs(docs);
          }

          // Stop polling if document is completed or failed
          if (doc && (doc.processing_status === 'completed' || doc.processing_status === 'failed')) {
            clearInterval(pollInterval);

            if (doc.processing_status === 'completed') {
              setSuccessMessage('Document processing completed!');
              setTimeout(() => setSuccessMessage(''), 3000);
            } else {
              setErrorMessage('Document processing failed');
              setTimeout(() => setErrorMessage(''), 5000);
            }
          }
        }
      } catch (err) {
        console.error('Polling error:', err);
      }
    }, 2000); // Poll every 2 seconds

    // Stop polling after 60 seconds max
    setTimeout(() => clearInterval(pollInterval), 60000);
  };

  const handleDrop = (e, documentType) => {
    e.preventDefault();
    e.stopPropagation();

    const files = Array.from(e.dataTransfer.files);
    files.forEach(file => {
      if (file.type === 'application/pdf' ||
          file.type === 'application/msword' ||
          file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
        handleFileUpload(file, documentType);
      } else {
        setErrorMessage(`File ${file.name} is not a supported format. Please upload PDF or Word documents.`);
        setTimeout(() => setErrorMessage(''), 5000);
      }
    });
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleFileInputChange = (e, documentType) => {
    const files = Array.from(e.target.files);
    files.forEach(file => handleFileUpload(file, documentType));
    e.target.value = ''; // Reset input
  };

  const handleView = async (documentId) => {
    try {
      setLoading(true);
      const response = await apiClient.viewCompanyDocument(documentId);
      if (response.success) {
        setSelectedDocument(response.data);
        setViewModalOpen(true);
      }
    } catch (err) {
      setErrorMessage(`Failed to load document: ${err.message}`);
      setTimeout(() => setErrorMessage(''), 5000);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (documentId, filename) => {
    try {
      const blob = await apiClient.downloadCompanyDocument(documentId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setErrorMessage(`Download failed: ${err.message}`);
      setTimeout(() => setErrorMessage(''), 5000);
    }
  };

  const handleDeleteClick = (documentId, documentName) => {
    setDocumentToDelete({ id: documentId, name: documentName });
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!documentToDelete) return;

    try {
      setLoading(true);
      await apiClient.deleteCompanyDocument(documentToDelete.id);
      setSuccessMessage('Document deleted successfully');
      setTimeout(() => setSuccessMessage(''), 5000);
      await loadDocuments();
      setDeleteModalOpen(false);
      setDocumentToDelete(null);
    } catch (err) {
      setErrorMessage(`Delete failed: ${err.message}`);
      setTimeout(() => setErrorMessage(''), 5000);
    } finally {
      setLoading(false);
    }
  };

  const cancelDelete = () => {
    setDeleteModalOpen(false);
    setDocumentToDelete(null);
  };

  const filteredPPDocs = pastPerformanceDocs
    .filter(doc =>
      doc.document_name.toLowerCase().includes(searchPP.toLowerCase()) ||
      doc.pp_customer?.toLowerCase().includes(searchPP.toLowerCase()) ||
      doc.pp_project_name?.toLowerCase().includes(searchPP.toLowerCase())
    )
    .sort((a, b) => a.document_name.localeCompare(b.document_name));

  const filteredResumeDocs = resumeDocs
    .filter(doc =>
      doc.document_name.toLowerCase().includes(searchResume.toLowerCase()) ||
      doc.resume_person_name?.toLowerCase().includes(searchResume.toLowerCase()) ||
      doc.resume_role_title?.toLowerCase().includes(searchResume.toLowerCase())
    )
    .sort((a, b) => {
      const nameA = a.resume_person_name || a.document_name;
      const nameB = b.resume_person_name || b.document_name;
      return nameA.localeCompare(nameB);
    });

  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const getProcessingBadge = (status) => {
    const colors = {
      completed: 'bg-green-100 text-green-800',
      processing: 'bg-blue-100 text-blue-800',
      pending: 'bg-yellow-100 text-yellow-800',
      failed: 'bg-red-100 text-red-800'
    };
    return (
      <span className={`px-2 py-1 rounded text-xs font-medium ${colors[status] || colors.pending}`}>
        {status}
      </span>
    );
  };

  return (
    <div className="space-y-8">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Past Performance Section */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-slate-900">
            <FontAwesomeIcon icon="trophy" className="mr-2 text-yellow-500" />
            Past Performance Documents ({filteredPPDocs.length})
          </h2>
          <input
            type="text"
            placeholder="Search past performance..."
            value={searchPP}
            onChange={(e) => setSearchPP(e.target.value)}
            className="px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
          />
        </div>

        {/* Upload Zone */}
        <div
          onDrop={(e) => handleDrop(e, 'past_performance')}
          onDragOver={handleDragOver}
          className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center bg-slate-50 hover:bg-slate-100 transition-colors cursor-pointer mb-4"
          onClick={() => document.getElementById('pp-file-input').click()}
        >
          <input
            id="pp-file-input"
            type="file"
            multiple
            accept=".pdf,.doc,.docx"
            onChange={(e) => handleFileInputChange(e, 'past_performance')}
            className="hidden"
          />
          {uploadingPP ? (
            <div className="flex items-center justify-center">
              <LoadingSpinner size="sm" />
              <span className="ml-2">Uploading...</span>
            </div>
          ) : (
            <>
              <FontAwesomeIcon icon="cloud-upload-alt" className="text-4xl text-slate-400 mb-2" />
              <p className="text-slate-600 font-medium">Drag & Drop Past Performance Documents</p>
              <p className="text-slate-500 text-sm">or click to browse (PDF, DOC, DOCX)</p>
            </>
          )}
        </div>

        {/* Document Table */}
        {loading && filteredPPDocs.length === 0 ? (
          <div className="text-center py-8">
            <LoadingSpinner />
          </div>
        ) : filteredPPDocs.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            No past performance documents uploaded yet
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-slate-700 uppercase tracking-wider">
                    Document Name
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-slate-700 uppercase tracking-wider">
                    Project / Customer
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-slate-700 uppercase tracking-wider">
                    Year
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-slate-700 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-slate-700 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200">
                {filteredPPDocs.map((doc) => (
                  <tr key={doc.document_id} className="hover:bg-slate-50">
                    <td className="px-4 py-2">
                      <div className="flex items-center">
                        <FontAwesomeIcon icon="file-alt" className="text-slate-400 mr-2" />
                        <span className="text-sm font-medium text-slate-900">{doc.document_name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-2">
                      <div className="text-sm text-slate-700">
                        {doc.pp_project_name && <div className="font-medium">{doc.pp_project_name}</div>}
                        {doc.pp_customer && <div className="text-slate-500">{doc.pp_customer}</div>}
                      </div>
                    </td>
                    <td className="px-4 py-2">
                      <span className="text-sm text-slate-700">{doc.pp_year || '-'}</span>
                    </td>
                    <td className="px-4 py-2">
                      {getProcessingBadge(doc.processing_status)}
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex justify-end space-x-1">
                        <button
                          onClick={() => handleView(doc.document_id)}
                          className="px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
                          title="View"
                        >
                          <FontAwesomeIcon icon="eye" />
                        </button>
                        <button
                          onClick={() => handleDownload(doc.document_id, doc.original_filename)}
                          className="px-2 py-1 text-xs bg-green-500 text-white rounded hover:bg-green-600 transition-colors"
                          title="Download"
                        >
                          <FontAwesomeIcon icon="download" />
                        </button>
                        <button
                          onClick={() => handleDeleteClick(doc.document_id, doc.document_name)}
                          className="px-2 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
                          title="Delete"
                        >
                          <FontAwesomeIcon icon="trash" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Resumes Section */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-slate-900">
            <FontAwesomeIcon icon="user-tie" className="mr-2 text-blue-500" />
            Resumes ({filteredResumeDocs.length})
          </h2>
          <input
            type="text"
            placeholder="Search resumes..."
            value={searchResume}
            onChange={(e) => setSearchResume(e.target.value)}
            className="px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
          />
        </div>

        {/* Upload Zone */}
        <div
          onDrop={(e) => handleDrop(e, 'resume')}
          onDragOver={handleDragOver}
          className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center bg-slate-50 hover:bg-slate-100 transition-colors cursor-pointer mb-4"
          onClick={() => document.getElementById('resume-file-input').click()}
        >
          <input
            id="resume-file-input"
            type="file"
            multiple
            accept=".pdf,.doc,.docx"
            onChange={(e) => handleFileInputChange(e, 'resume')}
            className="hidden"
          />
          {uploadingResume ? (
            <div className="flex items-center justify-center">
              <LoadingSpinner size="sm" />
              <span className="ml-2">Uploading...</span>
            </div>
          ) : (
            <>
              <FontAwesomeIcon icon="cloud-upload-alt" className="text-4xl text-slate-400 mb-2" />
              <p className="text-slate-600 font-medium">Drag & Drop Resumes</p>
              <p className="text-slate-500 text-sm">or click to browse (PDF, DOC, DOCX)</p>
            </>
          )}
        </div>

        {/* Document Table */}
        {loading && filteredResumeDocs.length === 0 ? (
          <div className="text-center py-8">
            <LoadingSpinner />
          </div>
        ) : filteredResumeDocs.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            No resumes uploaded yet
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-slate-700 uppercase tracking-wider">
                    Document Name
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-slate-700 uppercase tracking-wider">
                    Person / Role
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-slate-700 uppercase tracking-wider">
                    Experience
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-slate-700 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-slate-700 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200">
                {filteredResumeDocs.map((doc) => (
                  <tr key={doc.document_id} className="hover:bg-slate-50">
                    <td className="px-4 py-2">
                      <div className="flex items-center">
                        <FontAwesomeIcon icon="id-card" className="text-slate-400 mr-2" />
                        <span className="text-sm font-medium text-slate-900">{doc.document_name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-2">
                      <div className="text-sm text-slate-700">
                        {doc.resume_person_name && <div className="font-medium">{doc.resume_person_name}</div>}
                        {doc.resume_role_title && <div className="text-slate-500">{doc.resume_role_title}</div>}
                      </div>
                    </td>
                    <td className="px-4 py-2">
                      <span className="text-sm text-slate-700">
                        {doc.resume_years_experience ? `${doc.resume_years_experience} years` : '-'}
                      </span>
                    </td>
                    <td className="px-4 py-2">
                      {getProcessingBadge(doc.processing_status)}
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex justify-end space-x-1">
                        <button
                          onClick={() => handleView(doc.document_id)}
                          className="px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
                          title="View"
                        >
                          <FontAwesomeIcon icon="eye" />
                        </button>
                        <button
                          onClick={() => handleDownload(doc.document_id, doc.original_filename)}
                          className="px-2 py-1 text-xs bg-green-500 text-white rounded hover:bg-green-600 transition-colors"
                          title="Download"
                        >
                          <FontAwesomeIcon icon="download" />
                        </button>
                        <button
                          onClick={() => handleDeleteClick(doc.document_id, doc.document_name)}
                          className="px-2 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
                          title="Delete"
                        >
                          <FontAwesomeIcon icon="trash" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Success Toast */}
      {successMessage && (
        <div className="fixed top-4 right-4 z-50 animate-fade-in">
          <div className="bg-green-500 text-white px-6 py-4 rounded-lg shadow-lg flex items-center space-x-3">
            <FontAwesomeIcon icon="check-circle" className="text-2xl" />
            <div>
              <p className="font-semibold">Success</p>
              <p className="text-sm">{successMessage}</p>
            </div>
            <button
              onClick={() => setSuccessMessage('')}
              className="ml-4 text-white hover:text-gray-200"
            >
              <FontAwesomeIcon icon="times" />
            </button>
          </div>
        </div>
      )}

      {/* Error Toast */}
      {errorMessage && (
        <div className="fixed top-4 right-4 z-50 animate-fade-in">
          <div className="bg-red-500 text-white px-6 py-4 rounded-lg shadow-lg flex items-center space-x-3">
            <FontAwesomeIcon icon="exclamation-circle" className="text-2xl" />
            <div>
              <p className="font-semibold">Error</p>
              <p className="text-sm">{errorMessage}</p>
            </div>
            <button
              onClick={() => setErrorMessage('')}
              className="ml-4 text-white hover:text-gray-200"
            >
              <FontAwesomeIcon icon="times" />
            </button>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModalOpen && documentToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                  <FontAwesomeIcon icon="exclamation-triangle" className="text-red-600 text-xl" />
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-slate-900 mb-2">Delete Document</h3>
                <p className="text-slate-600 mb-4">
                  Are you sure you want to delete <strong>"{documentToDelete.name}"</strong>? This action cannot be undone.
                </p>
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={cancelDelete}
                    className="px-4 py-2 bg-slate-200 text-slate-700 rounded hover:bg-slate-300 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmDelete}
                    disabled={loading}
                    className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition-colors disabled:opacity-50 flex items-center"
                  >
                    {loading ? (
                      <>
                        <LoadingSpinner size="sm" />
                        <span className="ml-2">Deleting...</span>
                      </>
                    ) : (
                      <>
                        <FontAwesomeIcon icon="trash" className="mr-2" />
                        Delete
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Modal */}
      {viewModalOpen && selectedDocument && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between">
              <h2 className="text-xl font-bold text-slate-900">{selectedDocument.document_name}</h2>
              <button
                onClick={() => setViewModalOpen(false)}
                className="text-slate-500 hover:text-slate-700"
              >
                <FontAwesomeIcon icon="times" className="text-xl" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto flex-1">
              {selectedDocument.document_type === 'past_performance' && selectedDocument.pp_description && (
                <div className="mb-4 p-4 bg-blue-50 rounded">
                  <h3 className="font-semibold text-slate-900 mb-2">AI-Extracted Summary</h3>
                  <p className="text-slate-700">{selectedDocument.pp_description}</p>
                  {selectedDocument.pp_customer && (
                    <p className="text-sm text-slate-600 mt-2"><strong>Customer:</strong> {selectedDocument.pp_customer}</p>
                  )}
                  {selectedDocument.pp_year && (
                    <p className="text-sm text-slate-600"><strong>Year:</strong> {selectedDocument.pp_year}</p>
                  )}
                </div>
              )}
              {selectedDocument.document_type === 'resume' && selectedDocument.resume_summary && (
                <div className="mb-4 p-4 bg-blue-50 rounded">
                  <h3 className="font-semibold text-slate-900 mb-2">AI-Extracted Summary</h3>
                  <p className="text-slate-700">{selectedDocument.resume_summary}</p>
                  {selectedDocument.resume_person_name && (
                    <p className="text-sm text-slate-600 mt-2"><strong>Name:</strong> {selectedDocument.resume_person_name}</p>
                  )}
                  {selectedDocument.resume_role_title && (
                    <p className="text-sm text-slate-600"><strong>Role:</strong> {selectedDocument.resume_role_title}</p>
                  )}
                  {selectedDocument.resume_key_skills && (
                    <p className="text-sm text-slate-600"><strong>Skills:</strong> {selectedDocument.resume_key_skills}</p>
                  )}
                </div>
              )}
              <div>
                <h3 className="font-semibold text-slate-900 mb-2">Full Text</h3>
                <div className="whitespace-pre-wrap text-sm text-slate-700 font-mono bg-slate-50 p-4 rounded max-h-96 overflow-y-auto">
                  {selectedDocument.extracted_text || 'Text extraction in progress...'}
                </div>
              </div>
            </div>
            <div className="p-4 border-t border-slate-200 flex justify-end space-x-2">
              <button
                onClick={() => handleDownload(selectedDocument.document_id, selectedDocument.original_filename)}
                className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
              >
                <FontAwesomeIcon icon="download" className="mr-2" />
                Download
              </button>
              <button
                onClick={() => setViewModalOpen(false)}
                className="px-4 py-2 bg-slate-500 text-white rounded hover:bg-slate-600"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyData;
